import type { ProgramExamResponse, ScheduledExamInfo, EvaluationExam, CurriculumTarget, GuideGenerationMode, GuideLength } from '../types/payloads/evaluacion.types';
import type { RoomState } from '../types/room.types';
import type { ParsedScoreSummary } from '../music/scoreImport/scoreAst.types';
import { normalizeJoinCode } from '../engine/codeGenerator';
import { resolveInteractiveRoomUrl, interactiveRoomConnectionErrorMessage } from './resolveInteractiveRoomUrl';
import { sanitizeCourseProfileForRequest } from '../schemas/courseProfileSchema';

const SESSION_ARCHETYPES = new Set(['destreza', 'indagacion', 'taller', 'evaluacion']);

/** flowProfile ids that sometimes leak into sessionConfig.archetype */
const FLOW_PROFILE_TO_ARCHETYPE: Record<string, string> = {
  scale_project: 'taller',
  measurement_inquiry: 'indagacion',
  source_inquiry: 'indagacion',
  workshop_scaffold: 'taller',
  skill_drill: 'destreza',
};

function sanitizeSessionConfig(raw: unknown): { archetype: string; variant?: string } | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  let archetype = String(row.archetype || '').trim();
  if (FLOW_PROFILE_TO_ARCHETYPE[archetype]) {
    archetype = FLOW_PROFILE_TO_ARCHETYPE[archetype]!;
  }
  if (!SESSION_ARCHETYPES.has(archetype)) return undefined;
  const out: { archetype: string; variant?: string } = { archetype };
  if (
    archetype === 'destreza' &&
    (row.variant === 'guiado' || row.variant === 'autonomo')
  ) {
    out.variant = String(row.variant);
  }
  return out;
}

function sanitizeCurriculumTarget(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const axis = String(row.axis || row.eje || '').trim();
  const oaCode = String(row.oaCode || row.oa || '').trim();
  if (!axis || !oaCode) return undefined;
  const next: Record<string, string> = { axis, oaCode };
  const oah = String(row.oahCode || '').trim();
  const oaa = String(row.oaaCode || '').trim();
  if (oah) next.oahCode = oah;
  if (oaa) next.oaaCode = oaa;
  return next;
}

/** Evita sessionSummary '' / pasos sin título / archetype inválido. */
function sanitizeLinkedSessionContext(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const ctx = { ...(raw as Record<string, unknown>) };
  const objective = String(ctx.sessionObjective || ctx.sessionTitle || 'Sesión').trim() || 'Sesión';
  ctx.sessionObjective = objective;
  ctx.sessionTitle = String(ctx.sessionTitle || objective).trim() || objective;
  ctx.sessionSummary =
    String(ctx.sessionSummary || '').trim() ||
    String(ctx.sessionObjective || '').trim() ||
    objective;
  ctx.unitTitle = String(ctx.unitTitle || 'Unidad').trim() || 'Unidad';
  ctx.courseId = String(ctx.courseId || '').trim() || 'course';
  ctx.unitId = String(ctx.unitId || '').trim() || 'unit';
  ctx.sessionId = String(ctx.sessionId || '').trim() || 'session';
  if (Array.isArray(ctx.sessionFlow)) {
    ctx.sessionFlow = ctx.sessionFlow
      .filter((step) => step && typeof step === 'object')
      .map((step) => {
        const row = step as Record<string, unknown>;
        const title = String(row.title || '').trim();
        if (!title) return null;
        return { ...row, title };
      })
      .filter(Boolean);
    if (!(ctx.sessionFlow as unknown[]).length) delete ctx.sessionFlow;
  }
  const sessionConfig = sanitizeSessionConfig(ctx.sessionConfig);
  if (sessionConfig) ctx.sessionConfig = sessionConfig;
  else delete ctx.sessionConfig;

  const curriculumTarget = sanitizeCurriculumTarget(ctx.curriculumTarget);
  if (curriculumTarget) ctx.curriculumTarget = curriculumTarget;
  else delete ctx.curriculumTarget;

  // visualAnchor vacío rompe el refine — omitir
  if (ctx.visualAnchor && typeof ctx.visualAnchor === 'object') {
    const va = ctx.visualAnchor as Record<string, unknown>;
    const imageUrl = String(va.imageUrl || '').trim();
    const wikiQuery = String(va.wikiQuery || '').trim();
    if (!imageUrl && !wikiQuery) delete ctx.visualAnchor;
  }

  return ctx;
}

export interface ExamJoinResponse {
  roomId: string;
  joinCode: string;
  accessCode: string;
  examTitle?: string;
  sessionKind: 'async_exam';
  participantId: string;
  sessionToken: string;
  stateVersion: number;
  state: RoomState;
}

function resolveBaseUrl(baseUrl?: string): string {
  return resolveInteractiveRoomUrl(baseUrl);
}

async function readJsonResponse<T extends Record<string, unknown>>(resp: Response): Promise<T> {
  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  if (!contentType.includes('application/json')) {
    if (/^\s*</.test(text)) {
      throw new Error('server_endpoint_unavailable');
    }
    throw new Error('invalid_server_response');
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('invalid_server_response');
  }
}

export async function lookupScheduledExam(
  accessCode: string,
  baseUrl?: string,
): Promise<ScheduledExamInfo | null> {
  const code = normalizeJoinCode(accessCode);
  const resp = await fetch(`${resolveBaseUrl(baseUrl)}/api/exams/lookup/${encodeURIComponent(code)}`);
  if (!resp.ok) return null;
  const json = await readJsonResponse<{ exam: ScheduledExamInfo }>(resp);
  return json.exam ?? null;
}

export async function programScheduledExam(
  exam: unknown,
  teacherId: string,
  baseUrl?: string,
): Promise<ProgramExamResponse> {
  const resp = await fetch(`${resolveBaseUrl(baseUrl)}/api/exams/program`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ exam, teacherId }),
  });
  const json = await readJsonResponse<{ error?: string } & ProgramExamResponse>(resp);
  if (!resp.ok) throw new Error(json.error || 'program_exam_failed');
  return json as ProgramExamResponse;
}

export async function joinScheduledExam(params: {
  accessCode: string;
  displayName: string;
  studentId?: string;
  identityKey?: string;
  baseUrl?: string;
}): Promise<ExamJoinResponse> {
  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/exams/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      accessCode: normalizeJoinCode(params.accessCode),
      displayName: params.displayName,
      studentId: params.studentId,
      identityKey: params.identityKey,
    }),
  });
  const json = await readJsonResponse<{ error?: string } & ExamJoinResponse>(resp);
  if (!resp.ok) throw new Error(json.error || 'exam_join_failed');
  return json as ExamJoinResponse;
}

export interface GuideBlueprintPreviewSlot {
  index: number;
  mode: string;
  modeLabel: string;
  phase: string;
  phaseLabel: string;
  purpose: string;
  hint: string;
}

export interface GuideBlueprintPreview {
  conceptType: string;
  conceptLabel: string;
  blockCount: number;
  includeReflection: boolean;
  source: string;
  slots: GuideBlueprintPreviewSlot[];
  archetypeId?: string;
  archetypeHints?: string[];
  suggestedCurriculumTarget?: CurriculumTarget | null;
  intentMismatch?: {
    expected: string;
    message: string;
  } | null;
  sessionPlanning?: {
    source: 'teacher_plan' | 'teacher_partial' | 'template';
    entorno: string;
    measureCount: number;
    milestonePhase: string;
    scaffoldDensity: string;
  };
}

/** Inferencia en vivo desde plan de sesión — panel de diagnóstico docente. */
export interface SessionPlanningInference {
  relevant: boolean;
  source: 'teacher_plan' | 'teacher_partial' | 'template';
  sourceLabel: string;
  entorno: string;
  entornoKey: string;
  measures: Array<{ label: string; raw: string; value: number; unit: string }>;
  measureDisplay: string[];
  measureCount: number;
  milestonePhase: string;
  milestoneLabel: string;
  scaffoldDensity: string;
  archetype: string | null;
  resources: string[];
  actionMessage: string;
}

export interface CurriculumCatalogOa {
  code: string;
  text: string;
  summary: string;
}

export interface CurriculumCatalogAxis {
  id: string;
  label: string;
  oas: CurriculumCatalogOa[];
}

export interface CurriculumCatalogOah {
  code: string;
  text: string;
  group: string;
}

export interface CurriculumCatalogOaa {
  code: string;
  text: string;
}

export interface CurriculumMatrixCatalog {
  key: string;
  levelLabel: string;
  axes: CurriculumCatalogAxis[];
  oahs: CurriculumCatalogOah[];
  oaas: CurriculumCatalogOaa[];
}

export async function fetchCurriculumCatalog(params: {
  level?: string;
  subjectTag?: string;
  baseUrl?: string;
}): Promise<CurriculumMatrixCatalog | null> {
  const level = String(params.level || '1basico').trim();
  const subjectTag = String(params.subjectTag || 'matematica').trim();
  const query = new URLSearchParams({ level, subjectTag });

  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/guides/curriculum/catalog?${query}`);
  } catch {
    throw new Error(interactiveRoomConnectionErrorMessage());
  }

  if (resp.status === 404) return null;

  const json = await readJsonResponse<{ ok?: boolean; catalog?: CurriculumMatrixCatalog; error?: string }>(
    resp,
  );
  if (resp.ok && json.ok && json.catalog) return json.catalog;
  if (json.error === 'curriculum_matrix_not_found') return null;
  throw new Error(interactiveRoomConnectionErrorMessage());
}

export type CurriculumSuggestResult = {
  curriculumTarget: CurriculumTarget;
  matchedOaText: string;
  score: number;
  replaced: boolean;
};

/** Elige el OA/eje del curso que mejor justifica el prompt docente. */
export async function suggestCurriculumTargetFromPrompt(params: {
  prompt?: string;
  contentContext?: string;
  level?: string;
  subjectTag?: string;
  currentTarget?: CurriculumTarget | null;
  baseUrl?: string;
}): Promise<CurriculumSuggestResult | null> {
  const prompt = String(params.contentContext || params.prompt || '').trim();
  const level = String(params.level || '').trim();
  const subjectTag = String(params.subjectTag || '').trim();
  if (prompt.length < 4 || !level || !subjectTag) return null;

  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/guides/curriculum/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        contentContext: prompt,
        level,
        subjectTag,
        currentTarget: params.currentTarget ?? null,
      }),
    });
  } catch {
    return null;
  }

  try {
    const json = await readJsonResponse<{
      ok?: boolean;
      suggestion?: CurriculumSuggestResult | null;
    }>(resp);
    if (!resp.ok || !json.ok) return null;
    return json.suggestion ?? null;
  } catch {
    return null;
  }
}

export async function previewGuideBlueprint(params: {
  prompt?: string;
  contentContext?: string;
  level?: string;
  subjectTag?: string;
  extension?: string;
  baseUrl?: string;
  generationMode?: GuideGenerationMode;
  guideLength?: GuideLength;
  curriculumTarget?: CurriculumTarget;
  linkedSessionContext?: import('../types/payloads/evaluacion.types').LinkedSessionContext;
}): Promise<GuideBlueprintPreview | null> {
  const trimmed = String(params.contentContext || params.prompt || '').trim();
  if (!trimmed) return null;

  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/guides/blueprint/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentContext: trimmed,
        prompt: trimmed,
        level: params.level,
        subjectTag: params.subjectTag,
        extension: params.extension,
        generationMode: params.generationMode,
        guideLength: params.guideLength,
        curriculumTarget: params.curriculumTarget,
        linkedSessionContext: params.linkedSessionContext,
      }),
    });
  } catch {
    return null;
  }

  const json = await readJsonResponse<{ ok?: boolean; preview?: GuideBlueprintPreview | null }>(resp);
  if (!resp.ok || !json.ok) return null;
  return json.preview ?? null;
}

export async function inferSessionContext(params: {
  sessionObjective?: string;
  sessionSummary?: string;
  sessionTitle?: string;
  foco?: string;
  evidencia?: string;
  title?: string;
  sessionConfig?: { archetype: string; variant?: string };
  subjectTag?: string;
  baseUrl?: string;
  signal?: AbortSignal;
}): Promise<SessionPlanningInference | null> {
  const sessionObjective = String(
    params.sessionObjective ?? params.foco ?? '',
  ).trim();
  const sessionSummary = String(params.sessionSummary ?? params.evidencia ?? '').trim();
  const sessionTitle = String(params.sessionTitle ?? params.title ?? '').trim();
  const combined = [sessionObjective, sessionSummary, sessionTitle].join(' ').trim();
  if (combined.length < 8) return null;

  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/guides/infer-context`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionObjective,
        sessionSummary,
        sessionTitle,
        sessionConfig: params.sessionConfig,
        subjectTag: params.subjectTag,
      }),
      signal: params.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    return null;
  }

  let json: { ok?: boolean; sessionPlanning?: SessionPlanningInference };
  try {
    json = await readJsonResponse<{
      ok?: boolean;
      sessionPlanning?: SessionPlanningInference;
    }>(resp);
  } catch {
    // Proxy/HTML 500 durante reinicio del backend → no tumbar el panel.
    return null;
  }
  if (!resp.ok || !json.ok || !json.sessionPlanning) return null;
  return json.sessionPlanning;
}

export async function generateExamWithAi(params: {
  prompt: string;
  contentContext?: string;
  teacherId?: string;
  subjectTag?: string;
  subjectExpertPrompt?: string;
  extension?: string;
  level?: string;
  focus?: string;
  enfoqueDidactico?: 'teorico' | 'practico' | 'indagacion' | string;
  mode?: 'teorico' | 'aprendizaje' | 'practico' | 'descubrimiento' | string;
  config?: { includeTheory?: boolean; includeActivities?: boolean };
  resourceType?: 'exam' | 'guide';
  musicPrefs?: { readingMethod?: string; graphicMode?: string };
  scoreContext?: {
    title?: string;
    summary?: ParsedScoreSummary;
    primaryPattern?: string[];
    timeSignature?: string;
    bpm?: number;
    llmBrief?: string;
    melodyNotes?: Array<{ id: string; pitch: string; label: string; duration: string }>;
  };
  knowledgeBase?: {
    text: string;
    fileName: string;
    charCount?: number;
    method?: string;
    truncated?: boolean;
  };
  /** Currículum declarado (default) vs. guía libre sin marco MINEDUC. */
  generationMode?: GuideGenerationMode;
  /** OA/OAH/OAA explícitos — bypass RAG cuando generationMode=curricular. */
  curriculumTarget?: CurriculumTarget;
  guideLength?: GuideLength;
  /** Modelo 2 — receta imprimible vs proyector. */
  deliveryFormat?: 'print' | 'interactive';
  deliveryProfile?: import('@interactive-room').DeliveryProfile;
  courseProfile?: import('../types/courseProfile.types').CourseProfile;
  linkedSessionContext?: import('../types/payloads/evaluacion.types').LinkedSessionContext;
  /** Ancla visual docente — URL o wikiQuery (prioridad sobre Content Pack). */
  visualAnchor?: {
    imageUrl?: string;
    wikiQuery?: string;
    note?: string;
  };
  baseUrl?: string;
}): Promise<{
  exam: EvaluationExam;
  attempts?: number;
  provider?: 'mistral' | 'scaffold' | 'topic_factory' | 'partial_factory';
  factoryFillCount?: number;
  /** true cuando blocksGenerated < targetBlocks tras factory fill / reintentos */
  isDegraded?: boolean;
  blocksGenerated?: number;
  targetBlocks?: number;
  degradedReason?: string;
  /** Origen del blueprint (transparencia; no forma parte del esquema EvaluationExam). */
  guideDesign?: {
    source: string;
    designerAttempted?: boolean;
    designerOk?: boolean;
    degradations?: string[];
    mutations?: Array<{
      enforcer: string;
      blockId: string | null;
      changeType: string;
      reason: string;
    }>;
  };
  repertoireSearch?: {
    confidence: string;
    verificationMode: string;
    sources: Array<{ title: string; url: string; snippet: string; provider: string; score: number }>;
    tavilyConfigured: boolean;
    fromCache?: boolean;
  };
  humanitiesResearch?: {
    confidence: string;
    sources: Array<{ title: string; url: string; snippet: string; provider: string }>;
    tavilyConfigured: boolean;
    fromCache?: boolean;
  };
  knowledgeBaseUsed?: boolean;
}> {
  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/exams/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: params.contentContext?.trim() || params.prompt,
        contentContext: params.contentContext?.trim() || undefined,
        teacherId: params.teacherId,
        subjectTag: params.subjectTag,
        subjectExpertPrompt: params.subjectExpertPrompt,
        extension: params.extension,
        level: params.level,
        focus: params.focus,
        enfoqueDidactico: params.enfoqueDidactico,
        mode: params.mode,
        config: params.config,
        resourceType: params.resourceType === 'guide' ? 'guide' : 'exam',
        musicPrefs: params.musicPrefs,
        scoreContext: params.scoreContext,
        knowledgeBase: params.knowledgeBase,
        generationMode: params.generationMode,
        guideLength: params.guideLength,
        curriculumTarget: sanitizeCurriculumTarget(params.curriculumTarget),
        deliveryFormat: params.deliveryFormat,
        deliveryProfile: params.deliveryProfile,
        courseProfile: sanitizeCourseProfileForRequest(params.courseProfile),
        linkedSessionContext: sanitizeLinkedSessionContext(params.linkedSessionContext),
        visualAnchor: (() => {
          const va = params.visualAnchor;
          if (!va) return undefined;
          const imageUrl = String(va.imageUrl || '').trim();
          const wikiQuery = String(va.wikiQuery || '').trim();
          if (!imageUrl && !wikiQuery) return undefined;
          return {
            ...(imageUrl ? { imageUrl } : {}),
            ...(wikiQuery ? { wikiQuery } : {}),
            ...(va.note ? { note: String(va.note).trim() } : {}),
          };
        })(),
      }),
    });
  } catch {
    throw new Error('Failed to fetch');
  }
  const json = await readJsonResponse<{
    error?: string;
    exam?: EvaluationExam;
    attempts?: number;
    provider?: 'mistral' | 'scaffold' | 'topic_factory' | 'partial_factory';
  factoryFillCount?: number;
    isDegraded?: boolean;
    blocksGenerated?: number;
    targetBlocks?: number;
    degradedReason?: string;
    guideDesign?: {
      source: string;
      designerAttempted?: boolean;
      designerOk?: boolean;
      degradations?: string[];
      mutations?: Array<{
        enforcer: string;
        blockId: string | null;
        changeType: string;
        reason: string;
      }>;
    };
    repertoireSearch?: {
      confidence: string;
      verificationMode: string;
      sources: Array<{ title: string; url: string; snippet: string; provider: string; score: number }>;
      tavilyConfigured: boolean;
      fromCache?: boolean;
    };
    humanitiesResearch?: {
      confidence: string;
      sources: Array<{ title: string; url: string; snippet: string; provider: string }>;
      tavilyConfigured: boolean;
      fromCache?: boolean;
    };
    knowledgeBaseUsed?: boolean;
  }>(resp);
  if (!resp.ok) {
    const code = String(json.error || json.code || 'generate_exam_failed').trim();
    const details = String(json.details || json.message || '').trim();
    // Empaqueta detalles para mapGenerateExamError (el UI solo ve Error.message).
    throw new Error(details ? `${code}::${details}` : code || 'generate_exam_failed');
  }
  if (!json.exam) throw new Error('generate_exam_failed');
  return {
    exam: json.exam,
    attempts: json.attempts,
    provider: json.provider,
    factoryFillCount: json.factoryFillCount,
    isDegraded: json.isDegraded,
    blocksGenerated: json.blocksGenerated,
    targetBlocks: json.targetBlocks,
    degradedReason: json.degradedReason,
    guideDesign: json.guideDesign,
    repertoireSearch: json.repertoireSearch,
    humanitiesResearch: json.humanitiesResearch,
    knowledgeBaseUsed: json.knowledgeBaseUsed,
  };
}

export async function generateBlockWithAi(params: {
  mode: string;
  userIntent: string;
  examTitle?: string;
  subjectTag?: string;
  subjectExpertPrompt?: string;
  level?: string;
  resourceType?: 'exam' | 'guide';
  blockIndex?: number;
  currentLabel?: string;
  guideContext?: string;
  musicPrefs?: { readingMethod?: string; graphicMode?: string };
  baseUrl?: string;
}): Promise<{
  label: string;
  mode: string;
  payload: unknown;
  attempts?: number;
  provider?: 'mistral';
}> {
  let resp: Response;
  try {
    resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/exams/block/generate-inline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: params.mode,
        userIntent: params.userIntent,
        examTitle: params.examTitle,
        subjectTag: params.subjectTag,
        subjectExpertPrompt: params.subjectExpertPrompt,
        level: params.level,
        resourceType: params.resourceType === 'guide' ? 'guide' : 'exam',
        blockIndex: params.blockIndex,
        currentLabel: params.currentLabel,
        guideContext: params.guideContext,
        musicPrefs: params.musicPrefs,
      }),
    });
  } catch {
    throw new Error('Failed to fetch');
  }
  const json = await readJsonResponse<{
    error?: string;
    label?: string;
    mode?: string;
    payload?: unknown;
    attempts?: number;
    provider?: 'mistral';
  }>(resp);
  if (!resp.ok) throw new Error(json.error || 'generate_block_failed');
  if (!json.payload) throw new Error('generate_block_failed');
  return {
    label: String(json.label || params.currentLabel || ''),
    mode: String(json.mode || params.mode),
    payload: json.payload,
    attempts: json.attempts,
    provider: json.provider,
  };
}

export function mapGenerateExamError(code: string): string {
  const raw = String(code || '').trim();
  const sep = raw.indexOf('::');
  const errorCode = (sep >= 0 ? raw.slice(0, sep) : raw).trim();
  const details = (sep >= 0 ? raw.slice(sep + 2) : '').trim();

  switch (errorCode) {
    case 'mistral_not_configured':
      return 'IA no disponible: configura MISTRAL_API_KEY en el servidor (archivo .env en la raíz del proyecto).';
    case 'mistral_auth_failed':
      return 'Tu MISTRAL_API_KEY fue rechazada (401). Crea una nueva en console.mistral.ai y ponla en el .env de la raíz del proyecto.';
    case 'ia_proxy_unavailable':
      return 'Proxy IA no disponible. Levanta el backend con npm run server (puerto 3001) o corrige MISTRAL_API_KEY.';
    case 'prompt_too_short':
    case 'user_intent_required':
      return 'Describe qué quieres generar (mínimo unas palabras claras sobre el tema).';
    case 'invalid_mode':
      return 'Este tipo de bloque no admite generación con IA en este contexto.';
    case 'stimulus_required':
      return 'El ejercicio cita un texto específico pero falta el poema/fuente en stimulus.content. Reformula o regenera el bloque.';
    case 'mode_required':
      return 'Falta el modo del bloque.';
    case 'plugin_runner_unavailable':
      return 'El motor de evaluaciones no está disponible en el servidor.';
    case 'server_endpoint_unavailable':
      return import.meta.env.PROD
        ? 'El servidor no respondió a tiempo. La generación puede tardar un par de minutos; espera e intenta de nuevo.'
        : 'El servidor de sala está desactualizado o apagado. Reinicia con npm run dev:sala.';
    case 'invalid_server_response':
      return import.meta.env.PROD
        ? 'Respuesta inválida del servidor. Intenta de nuevo en unos segundos.'
        : 'Respuesta inválida del servidor. Verifica que interactive-room esté corriendo en el puerto 8790.';
    case 'curriculum_not_justified':
      return (
        details ||
        'El pedido no justifica el OA / marco curricular elegido. Alinea el tema al OA, cámbialo en el panel curricular, o usa modo independiente.'
      );
    case 'validation_failed':
      return (
        details ||
        'La solicitud no pasó validación (nivel, asignatura, prompt u opciones). Revisa el formulario e intenta de nuevo.'
      );
    case 'level_required':
    case 'level requerido':
    case 'subject_required':
    case 'subjectTag_required':
    case 'subjectTag requerido':
    case 'material_identity_incomplete':
      return details || 'Faltan nivel o asignatura para generar. Complétalos en el curso o vuelve a generar el material.';
    case 'level inválido':
    case 'subjectTag inválido':
      return details || 'Nivel o asignatura no válidos. Revisa el perfil del curso.';
    case 'generate_exam_failed':
      return details || 'No se pudo generar el material. Intenta reformular el pedido.';
    case 'Failed to fetch':
    case 'NetworkError when attempting to fetch resource.':
      return interactiveRoomConnectionErrorMessage();
    default:
      if (/failed to fetch|networkerror|load failed/i.test(errorCode)) {
        return interactiveRoomConnectionErrorMessage();
      }
      // Mensaje ya humanizado (español con espacios) o detalles empaquetados.
      if (details) return details;
      if (/\s/.test(errorCode) && !errorCode.includes('_')) return errorCode;
      return errorCode.includes('_')
        ? 'No se pudo generar el material. Intenta reformular el pedido.'
        : errorCode;
  }
}

export function mapExamJoinError(code: string): string {
  switch (code) {
    case 'ACCESS_CODE_INVALID':
      return 'Código no encontrado. El profesor debe publicar la evaluación con «Publicar evaluación async» antes de compartir el código.';
    case 'EXAM_ROOM_NOT_FOUND':
      return 'Esta evaluación ya no está disponible en el servidor.';
    case 'EXAM_ENDED':
      return 'La evaluación ya finalizó.';
    case 'ACCESS_CODE_TAKEN':
      return 'Ese código ya está en uso por otra evaluación.';
    case 'exam_requires_blocks':
      return 'La evaluación necesita al menos un bloque antes de publicar.';
    default:
      return code;
  }
}

export function mapRoomJoinError(code: string): string {
  switch (code) {
    case 'JOIN_CODE_INVALID':
      return 'Código de sala no válido. Si el servidor se reinició, el profesor debe crear una nueva sala en vivo. Para evaluaciones async usa la pestaña «Evaluación async».';
    case 'USE_EXAM_JOIN':
      return 'Este código es de evaluación programada. Cambia a «Evaluación async» en la pantalla de Campo.';
    case 'ROOM_ENDED':
      return 'La sesión en vivo ya terminó.';
    default:
      return code;
  }
}
